import { computed, inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { RsOverviewServerSideStateManager } from '@rs-shared-lib/abstract-classes/rs-overview-state/rs-overview-server-side-state-manager';
import { RsPersistOverviewState } from '@rs-shared-lib/abstract-classes/rs-overview-state/persistency/rs-persist-overview-state.decorator';
import { rsCountFilters } from '@rs-shared-lib/utils/count-filters/count-filters';
import { UserService } from '@core/services/user.service';
import { TranslationService, RsUtils } from '@lib/rs-ngx';
import { CustomComment } from '@shared/models/custom-comments/custom-comment';
import { MRT_DEFAULT_PAGE_SIZE, MRT_PAGE_SIZE_OPTIONS } from '@shared/constants/mrt-page-size-options';
import { environment } from '@environments/environment';
import { ServerSideQueryParams } from '@rs-shared-lib/abstract-classes/rs-overview-state/models/rs-overview-server-side-state-manager';
import { isBoolean } from 'lodash';

export type CustomCommentsFilters = {
  keyword: string;
  active: boolean | null;
};

@RsPersistOverviewState({
  storageKeySuffix: 'MrtLeasingCompanyCustomComments',
  includeUserContext: () => inject(UserService).getCurrentUser()!.loginString,
})
@Injectable()
export class CustomCommentsOverviewStateService extends RsOverviewServerSideStateManager<CustomComment, CustomCommentsFilters> {
  public readonly filterCount = computed(() => {
    const { keyword, active } = this.filters();
    return rsCountFilters({ keyword }) + (isBoolean(active) ? 1 : 0);
  });

  constructor() {
    const http = inject(HttpClient);
    const currentLanguage = inject(TranslationService).currentLang;

    super({
      serviceMethod: (params: ServerSideQueryParams<CustomCommentsFilters>) => {
        const {
          _page, _pageSize, _sortBy, _sortDirection, ...restFilters
        } = params;
        const filters = RsUtils.trimEmptyHttpParams({
          ...restFilters,
          page: _page,
          resultsPerPage: _pageSize,
          sortBy: _sortBy,
          sortDirection: _sortDirection,
          userLanguage: currentLanguage(),
        });

        return forkJoin([
          http.get<CustomComment[]>(`${environment.apiUrl}/leasing-company/custom-comment`, { params: filters }),
          http.get<number>(`${environment.apiUrl}/leasing-company/custom-comment/pagecount`, { params: filters }),
        ]).pipe(
          map(([data, pageCount]) =>
            new HttpResponse<CustomComment[]>({
              body: data,
              headers: new HttpHeaders({
                resultcount: String(pageCount * _pageSize),
                pagecount: String(pageCount),
              }),
            }),
          ),
        );
      },
      initialFiltersState: {
        keyword: '',
        active: null,
      },
      initialSortingState: { active: 'KEYWORD', direction: 'asc' },
      initialPagination: { pageIndex: 0, pageSize: MRT_DEFAULT_PAGE_SIZE },
      config: { pageSizeOptions: MRT_PAGE_SIZE_OPTIONS },
    });
  }
}
